import { SwapOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Checkbox, DatePicker, Flex, Form, List, Modal, Segmented, Select, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useRef } from 'react';
import type { Allocation, AllocationChange } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';
import { useBulkChange } from './BulkConfirm';
import { WEEKDAYS } from './PlanningTable';
import { DATE, DISPLAY_DATE, gridQuery, homeTeam, hours, projectLabel, transferMessage, useFeedback, UserSelect, useProjects, useTeams } from './shared';

type Mode = 'work' | 'teams';
const sum = (rows: Allocation[]) => rows.reduce((total, row) => total + row.minutes, 0);
const MAX_DAYS = 366;

/**
 * Swaps two technicians: their planned work over a period (each allocation keeps its project, date, hours and time slot)
 * or their home teams from a date. Work swaps are previewed with the resulting overloads and saved in one transaction.
 */
const TechnicianSwapModal: React.FC<{ userId?: string; userName?: string; from: string; to: string; onClose: () => void }> = ({ userId, userName, from, to, onClose }) => {
  const { data: teams = [] } = useTeams(); const { data: projects = [] } = useProjects(); const feedback = useFeedback();
  const [form] = Form.useForm(); const bulk = useBulkChange(onClose);
  const names = useRef(new Map<string, string>(userId && userName ? [[userId, userName]] : []));
  const mode: Mode = Form.useWatch('mode', form) ?? 'work';
  const first: string | undefined = Form.useWatch('first', form); const second: string | undefined = Form.useWatch('second', form);
  const period: [dayjs.Dayjs, dayjs.Dayjs] | undefined = Form.useWatch('period', form);
  const projectIds: string[] = Form.useWatch('projectIds', form) || []; const keepTeam: boolean = Form.useWatch('keepTeam', form) ?? true;
  const effective: dayjs.Dayjs | undefined = Form.useWatch('effectiveFrom', form);
  const [start, end] = period?.[0] && period?.[1] ? [period[0].format(DATE), period[1].format(DATE)] : [from, to];
  const validPeriod = dayjs(end).diff(dayjs(start), 'day') < MAX_DAYS;
  const { data: grid, isFetching } = useQuery({ ...gridQuery(start, end), enabled: mode === 'work' && validPeriod });
  const swapTeams = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });

  const nameOf = (id?: string) => (id && (names.current.get(id) || grid?.people.find(person => person.id === id)?.name)) || '';
  const rowsOf = (id?: string) => (grid?.allocations || []).filter(row => row.userId === id && (!projectIds.length || projectIds.includes(row.projectId)))
    .sort((a, b) => a.date.localeCompare(b.date) || a.projectNumber.localeCompare(b.projectNumber));
  const firstRows = rowsOf(first), secondRows = rowsOf(second);
  const days = [...new Set([...firstRows, ...secondRows].map(row => row.date))].sort();
  const date = effective?.format(DATE) || from;
  const firstHome = first ? homeTeam(teams, first, date) : undefined, secondHome = second ? homeTeam(teams, second, date) : undefined;
  // Same rule as replacing a technician: keep the planning team (borrowing) or follow the new person's home team that day.
  const reassign = (row: Allocation, user: string): AllocationChange => {
    const home = homeTeam(teams, user, row.date);
    return { id: row.id, expectedVersion: row.rowVersion, userId: user, ...(keepTeam ? {} : home ? { teamId: home.id } : { clearTeam: true }) };
  };
  const projectsOf = (rows: Allocation[]) => [...new Set(rows.map(row => row.projectNumber))].join(', ');

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (values.first === values.second) throw new Error('Selectati doi tehnicieni diferiti.');
      if (mode === 'teams') {
        swapTeams.mutate(async () => {
          const result = await api.swap({ userId: values.first, otherUserId: values.second, effectiveFrom: values.effectiveFrom.format(DATE) });
          feedback.success(transferMessage(result.affectedAllocations)); onClose();
        });
        return;
      }
      if (!firstRows.length && !secondRows.length) throw new Error('Nu exista alocari de schimbat in perioada selectata.');
      bulk.check({ changes: [...firstRows.map(row => reassign(row, second!)), ...secondRows.map(row => reassign(row, first!))] },
        `Schimba lucrarile: ${nameOf(first)} ⇄ ${nameOf(second)}`,
        <Space direction='vertical' size={0}>
          <span>{nameOf(first)} → {nameOf(second)}: {firstRows.length} zile · {hours(sum(firstRows))}</span>
          <span>{nameOf(second)} → {nameOf(first)}: {secondRows.length} zile · {hours(sum(secondRows))}</span>
        </Space>);
    } catch (error) { feedback.error(error); }
  };

  const person = (name: 'first' | 'second', label: string) => <Form.Item name={name} label={label} rules={[{ required: true, message: 'Selectati tehnicianul.' }]}>
    <UserSelect placeholder='Selectati tehnicianul' onPicked={options => options.forEach(option => names.current.set(option.value, option.label.split(' · ')[0]))}
      describe={id => homeTeam(teams, id, mode === 'teams' ? date : start)?.name || 'Fara echipa'} />
  </Form.Item>;
  return <>
    <Modal open title={<span><SwapOutlined /> Schimb intre doi tehnicieni</span>} onCancel={onClose} onOk={submit} width={680} destroyOnHidden
      okText={mode === 'work' ? 'Verifica schimbul' : 'Schimba echipele'} cancelText='Renunta' confirmLoading={bulk.busy || swapTeams.isPending}>
      <Form form={form} layout='vertical' initialValues={{ mode: 'work', first: userId, period: [dayjs(from), dayjs(to)], keepTeam: true, effectiveFrom: dayjs(from) }}>
        <Form.Item name='mode'>
          <Segmented<Mode> block options={[{ value: 'work', label: 'Lucrarile planificate' }, { value: 'teams', label: 'Echipele de baza' }]} />
        </Form.Item>
        <Flex gap={12}>
          <div style={{ flex: 1, minWidth: 0 }}>{person('first', 'Tehnician 1')}</div>
          <SwapOutlined style={{ marginTop: 38, color: '#722ed1' }} />
          <div style={{ flex: 1, minWidth: 0 }}>{person('second', 'Tehnician 2')}</div>
        </Flex>
        {mode === 'work' && <>
          <Form.Item name='period' label='Perioada' rules={[{ required: true }, { validator: () => validPeriod ? Promise.resolve() : Promise.reject(new Error('Selectati cel mult un an.')) }]}>
            <DatePicker.RangePicker format={DISPLAY_DATE} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='projectIds' label='Doar proiectele (optional)'>
            <Select mode='multiple' allowClear showSearch optionFilterProp='label' placeholder='Toate proiectele' maxTagCount='responsive'
              options={projects.map(item => ({ value: item.id, label: projectLabel(item) }))} />
          </Form.Item>
          <Form.Item name='keepTeam' valuePropName='checked' extra='Debifat: fiecare alocare trece pe echipa de baza a noului tehnician din ziua respectiva.'>
            <Checkbox>Pastreaza echipele de planificare (imprumut)</Checkbox>
          </Form.Item>
        </>}
        {mode === 'teams' && <Form.Item name='effectiveFrom' label='Incepand cu data' rules={[{ required: true }]}>
          <DatePicker format={DISPLAY_DATE} />
        </Form.Item>}
      </Form>
      {mode === 'work' && first && second && <Spin spinning={isFetching}>
        <Alert type={firstRows.length || secondRows.length ? 'info' : 'warning'} showIcon style={{ marginBottom: 8 }}
          message={firstRows.length || secondRows.length ? 'Fiecare alocare isi pastreaza proiectul, data, orele si intervalul orar.' : 'Niciun tehnician nu are alocari in perioada selectata.'}
          description={<Space direction='vertical' size={0}>
            <span>{nameOf(first)} → {nameOf(second)}: {firstRows.length} zile · {hours(sum(firstRows))}{firstRows.length ? ` · ${projectsOf(firstRows)}` : ''}</span>
            <span>{nameOf(second)} → {nameOf(first)}: {secondRows.length} zile · {hours(sum(secondRows))}{secondRows.length ? ` · ${projectsOf(secondRows)}` : ''}</span>
          </Space>} />
        {days.length > 0 && <List size='small' bordered style={{ maxHeight: 220, overflowY: 'auto' }} dataSource={days} renderItem={day => {
          const text = (rows: Allocation[]) => rows.filter(row => row.date === day).map(row => `${row.projectNumber} ${hours(row.minutes)}`).join(', ') || '—';
          return <List.Item style={{ fontSize: 12 }}>
            <Typography.Text strong style={{ width: 90 }}>{WEEKDAYS[dayjs(day).day()]} {dayjs(day).format('DD.MM')}</Typography.Text>
            <span style={{ flex: 1 }}>{text(firstRows)}</span><SwapOutlined style={{ margin: '0 8px', color: '#8c8c8c' }} /><span style={{ flex: 1 }}>{text(secondRows)}</span>
          </List.Item>;
        }} />}
      </Spin>}
      {mode === 'teams' && first && second && <Alert type={firstHome && secondHome && firstHome.id !== secondHome.id ? 'info' : 'warning'} showIcon
        message={firstHome && secondHome && firstHome.id !== secondHome.id
          ? `${nameOf(first)}: ${firstHome.name} → ${secondHome.name} · ${nameOf(second)}: ${secondHome.name} → ${firstHome.name}`
          : 'Ambele persoane trebuie sa apartina unor echipe diferite la data schimbului.'}
        description='Alocarile existente raman neschimbate. Pentru a muta si lucrarile, folositi Lucrarile planificate.' />}
    </Modal>
    {bulk.confirm}
  </>;
};
export default TechnicianSwapModal;
